import { DragEndEvent, DragOverEvent } from "@dnd-kit/core";
import { arrayMove, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import type { TableProps } from "../../../../utils/local-antd";
import debounce from "lodash/debounce";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button } from "../../../../components/Button";
import { Flex } from "../../../../components/Flex";
import { DNDContext } from "../../../../components/dnd/DNDContext";
import { DNDSortableContext } from "../../../../components/dnd/DNDSortableContext";
import { AtiraTable } from "../../../../components/table/AtiraTable";
import { EntriesTableSettings } from "../../../../model/entries-table-settings/EntriesTableSettings";import { Entry } from "../../../../model/entry/Entry";
import { entrySliceSelectors } from "../../../../redux/entry/entry.selector";
import { entryActions } from "../../../../redux/entry/entry.slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { Spacing } from "../../../../theme/Spacing";
import { TestIDs } from "../../../../tests/types/testIds";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { canHideTableColumn } from "../../utils/tables.util";
import { ADD_COLUMN_KEY } from "./constants";
import { EntriesTableWithDNDBodyCell } from "./EntriesTableWithDNDBodyCell";
import { DragIndexContext } from "./EntriesTableWithDNDContext";
import { EntriesTableWithDNDHeaderCell } from "./EntriesTableWithDNDHeaderCell";
import { EntiresTableWithDNDOverlay } from "./EntriesTableWithDNDOverlay";
import { DragIndexState } from "./types/DragIndexState";

interface EntiresTableWithDNDProps extends Omit<TableProps, "columns"> {
  data: Entry[];
  columns: TableProps["columns"];
  loading?: boolean;
  selectedRowKeys?: React.Key[];
  onSelectedRowKeysChange?: ATVoidFunction<React.Key[]>;
  /** Shows a "+" column at the end of the table that calls this. Resolves to whether a column was added. */
  onAddColumn?: () => Promise<boolean>;
  /** Gives every column that can be hidden a "-" in its header that calls this with its field. Resolves to whether it was hidden. */
  onHideColumn?: (field: string) => Promise<boolean>;
  /** Gives every custom field's header a pen that renames it in place, calling this with its field and the new name. Resolves to whether it was renamed. */
  onRenameColumn?: (field: string, label: string) => Promise<boolean>;
}

const AddColumnButton = styled(Button)`
  margin: 0;
  padding: ${Spacing.xs} ${Spacing.s};
`;

const StyledTableWrapper = styled.div<{ isDataEmpty: boolean }>`
  .ant-table-tbody > tr.entry-marked-for-deletion {
    background-image: ${({ theme }) => `
      repeating-linear-gradient(
        45deg,
        ${theme.white},
        ${theme.white} 6px,
        ${theme.sub} 6px,
        ${theme.sub} 7px
      )
    `};

    > td.ant-table-cell {
      background-image: inherit !important;

      &.lead-cell,
      &.junk-cell,
      &.contact-cell {
        background-image: none !important;
      }
    }

    > td:first-child {
      position: relative;

      &::after {
        content: "";
        position: absolute;
        inset-block: 0;
        inset-inline-start: 0;
        width: 5px;
        height: 100%;
        background-color: ${({ theme }) => theme.danger};
      }
    }

    .fixed-cell button,
    input,
    .ant-picker {
      background-color: ${({ theme }) => theme.white} !important;
      border: 1px solid ${({ theme }) => theme.textColor || theme.darkSub} !important;
    }
  }

  .ant-table-tbody > tr > td.ant-table-cell {
    &.lead-cell .actionable-cell {
      background-color: ${({ theme }) => theme.lead} !important;
      color: ${({ theme }) => theme.onLead} !important;

      svg {
        color: ${({ theme }) => theme.lead} !important;
      }
    }
    &.contact-cell .actionable-cell {
      background-color: ${({ theme }) => theme.contact} !important;
      color: ${({ theme }) => theme.onContact} !important;

      svg {
        color: ${({ theme }) => theme.contact} !important;
      }
    }
    &.junk-cell .actionable-cell {
      background-color: ${({ theme }) => theme.junk} !important;
      color: ${({ theme }) => theme.onJunk} !important;

      svg {
        color: ${({ theme }) => theme.junk} !important;
      }
    }
    &.high-priority-task {
      background-color: ${({ theme }) => theme.danger} !important;
    }
    &.medium-priority-task {
      background-color: ${({ theme }) => theme.warning} !important;
    }
    &.low-priority-task {
      background-color: ${({ theme }) => theme.success} !important;
    }
  }

  .ant-table-row:hover {
    .custom-field-cell input,
    .custom-field-cell .ant-picker {
      border-color: ${({ theme }) => theme.darkerSub} !important;
    }
  }
`;

type ColumnActions = {
  hide?: (field: string) => Promise<boolean>;
  rename?: (field: string, label: string) => Promise<boolean>;
};

const withDragIds = (
  columns: TableProps["columns"],
  actions: ColumnActions = {},
) =>
  (columns || []).map((c, i) => {
    const settingsColumn = (
      c as { data?: EntriesTableSettings["columns"][number] }
    ).data;
    const onHide =
      actions.hide && settingsColumn && canHideTableColumn(settingsColumn)
        ? () => actions.hide!(settingsColumn.field)
        : undefined;
    const onRename =
      actions.rename && settingsColumn?.custom
        ? (label: string) => actions.rename!(settingsColumn.field, label)
        : undefined;

    return {
      ...c,
      // Not the column's key: cells only re-render when their row changes, so
      // a key that moves to another column (as positions do when one is added
      // or hidden) leaves its cells showing - and saving - the old column
      dragId: `${i}`,
      onHeaderCell: () => ({
        id: `${i}`,
        field: settingsColumn?.field,
        label: settingsColumn?.label,
        onHide,
        onRename,
      }),
      onCell: () => ({ id: `${i}` }),
    };
  });

export const EntiresTableWithDND: React.FC<EntiresTableWithDNDProps> = ({
  data,
  columns: _columns,
  loading = false,
  selectedRowKeys,
  onSelectedRowKeysChange,
  onAddColumn,
  onHideColumn,
  onRenameColumn,
  ...props
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const isMember = useAppSelector(userSliceSelectors.selectIsUserOrgMember);
  const tableSettings = useAppSelector(
    entrySliceSelectors.selectUserEntryTableSettings,
  );

  // The columns below are built once and re-seeded only when the settings
  // change, so they must not capture handlers that go stale.
  const onHideColumnRef = useRef(onHideColumn);
  onHideColumnRef.current = onHideColumn;
  const onRenameColumnRef = useRef(onRenameColumn);
  onRenameColumnRef.current = onRenameColumn;
  const canHideColumns = Boolean(onHideColumn);
  const canRenameColumns = Boolean(onRenameColumn);
  const columnActions = useMemo<ColumnActions>(
    () => ({
      hide: canHideColumns
        ? (field) => onHideColumnRef.current?.(field) ?? Promise.resolve(false)
        : undefined,
      rename: canRenameColumns
        ? (field, label) =>
            onRenameColumnRef.current?.(field, label) ?? Promise.resolve(false)
        : undefined,
    }),
    [canHideColumns, canRenameColumns],
  );

  const [columns, setColumns] = useState(() =>
    withDragIds(_columns, columnActions),
  );

  // The columns are copied into local state so a drag-reorder shows instantly,
  // which would otherwise leave a renamed or toggled column stale until the
  // next mount. Re-seed them whenever what the settings control changes - not
  // on every `_columns` identity change, which would throw away a reorder that
  // is still waiting on its debounced save.
  const settingsSignature = JSON.stringify(
    (tableSettings?.columns || []).map(({ field, label, enabled, order }) => [
      field,
      label,
      enabled,
      order,
    ]),
  );
  const seededSignature = useRef(settingsSignature);

  useEffect(() => {
    if (seededSignature.current === settingsSignature) {
      return;
    }

    seededSignature.current = settingsSignature;
    setColumns(withDragIds(_columns, columnActions));
  }, [settingsSignature, _columns, columnActions]);

  const [dragIndex, setDragIndex] = useState<DragIndexState>({
    active: -1,
    over: -1,
  });

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      onSelectedRowKeysChange?.(newSelectedRowKeys);
    },
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      let newColumns: typeof columns = [];

      setColumns((prevState) => {
        const activeIndex = prevState.findIndex(
          (i) => i.dragId === active?.id,
        );
        const overIndex = prevState.findIndex((i) => i.dragId === over?.id);
        const cols = arrayMove(prevState, activeIndex, overIndex);
        newColumns = cols;
        return cols;
      });

      debouncedOnUpdateColumnsOrder(newColumns);
    }
    setDragIndex({ active: -1, over: -1 });
  };

  const onDragOver = ({ active, over }: DragOverEvent) => {
    const activeIndex = columns.findIndex((i) => i.dragId === active.id);
    const overIndex = columns.findIndex((i) => i.dragId === over?.id);
    setDragIndex({
      active: active.id,
      over: over?.id,
      direction: overIndex > activeIndex ? "right" : "left",
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnUpdateColumnsOrder = useCallback(
    debounce(async (newColumns: typeof columns) => {
      try {
        await dispatch(
          entryActions.updateUserTableSettings({
            userId,
            tableId: tableSettings?._id!,
            columns: newColumns
              .filter((c: any) => c.data.field !== "assignee")
              .map((c: any, index) => ({
                ...c.data,
                order: index,
              })) as Partial<EntriesTableSettings["columns"]>,
          }),
        ).unwrap();
        await dispatch(entryActions.getUserTableSettings({ userId })).unwrap();
      } catch (e: any) {
        AtiraToast.apiError(e);
        console.log(e);
      }
    }, 1000),
    [dispatch, tableSettings?._id, userId],
  );

  const [addingColumn, setAddingColumn] = useState(false);

  const addNewColumn = async () => {
    setAddingColumn(true);

    // A column that was added is still on its way in, so the spinner stays
    // until the columns below have been re-seeded (the effect that follows).
    if (!(await onAddColumn?.())) {
      setAddingColumn(false);
    }
  };

  useEffect(() => {
    setAddingColumn(false);
  }, [columns]);

  const addColumn = {
    key: ADD_COLUMN_KEY,
    title: (
      <AddColumnButton
        icon={faPlus}
        info={t("common.add_column")}
        loading={addingColumn}
        aria-busy={addingColumn}
        onClick={addNewColumn}
        dataTestId={TestIDs.entries.table.addColumn}
      />
    ),
    width: 64,
    align: "center" as const,
    onHeaderCell: () => ({ id: ADD_COLUMN_KEY }),
    onCell: () => ({ id: ADD_COLUMN_KEY }),
    render: () => null,
  };

  return (
    <Flex flexDirection="column" width="100%">
      <StyledTableWrapper isDataEmpty={!data.length}>
        <DNDContext onDragEnd={onDragEnd} onDragOver={onDragOver}>
          <DNDSortableContext
            items={data.map((x) => x._id)}
            strategy={horizontalListSortingStrategy}
          >
            <DragIndexContext.Provider value={dragIndex}>
              <AtiraTable
                data={data.map((x) => ({ ...x, key: x._id }))}
                rowSelection={rowSelection}
                columns={[
                  ...columns.filter((col: any) => col?.data?.enabled),
                  ...(onAddColumn ? [addColumn] : []),
                ]}
                loading={loading}
                emptyText={
                  isMember ? t("entries.no_entries.member") : undefined
                }
                components={{
                  header: { cell: EntriesTableWithDNDHeaderCell },
                  body: { cell: EntriesTableWithDNDBodyCell },
                }}
                rowClassName={(record: Entry) =>
                  record.markedForDeletion ? "entry-marked-for-deletion" : ""
                }
                {...(props as any)}
              />
            </DragIndexContext.Provider>
          </DNDSortableContext>
          <EntiresTableWithDNDOverlay columns={columns} dragIndex={dragIndex} />
        </DNDContext>
      </StyledTableWrapper>
    </Flex>
  );
};
